import React, { useState, useRef, useEffect } from 'react';
import { string, oneOf, func, bool } from 'prop-types';
import { PRODUCT_ADD_ON_IDENTIFIER } from '@thd-olt-functional/utils';
import { useLayoutGroupProps } from '@thd-olt-component-react/layout-manager';
import { ProductAddOnsPod } from '../../common/product-add-ons-pod';
import { ICONS } from '../../../utils/product-add-on-constants';
import { ProtectionPlanTooltip } from './ProtectionPlanTooltip';
import { CurrentProtectionPlanTooltip } from './CurrentProtectionPlanTooltip';

const { HDPP } = PRODUCT_ADD_ON_IDENTIFIER;

export const HomeDepotProtectionPlan = (props) => {

  const {
    itemId,
    channel,
    term,
    price,
    onChange,
    url,
  } = props;
  const { isNewHdppProviderEnabled } = useLayoutGroupProps({ props });
  const [isHdppSelected, setIsHdppSelected] = useState(false);
  const [isDualPathSelected, setIsDualPathSelected] = useState(null);
  const tooltipButtonRef = useRef(null);

  const checkIsPastLaunchTimeAndDay = () => {
    /*
       Jan 31 2021 11:59 PM EST
       Jan 31 2021 10:59 PM CST
       Jan 31 2021 8:59 PM PST
    */

    const newProviderLaunchTimeAndDay = new Date(Date.UTC(2021, 1, 1, 4, 59, 59));
    const currentTime = new Date();
    return currentTime > newProviderLaunchTimeAndDay;
  };

  const displayNewHdppProvider = isNewHdppProviderEnabled && checkIsPastLaunchTimeAndDay();
  const iconUrl = displayNewHdppProvider ? ICONS.hdpp : ICONS.currentHdpp;
  const subHeading = displayNewHdppProvider ? 'Select a Home Depot Protection Plan by Allstate for:'
    : 'Select a Home Depot Protection Plan for:';
  /** Props for checkbox component */
  const userOptions = [{
    id: itemId,
    value: 'hdpp-accept',
    label: `${term} Year / `,
    price,
    checked: isHdppSelected,
  },
  {
    value: 'hdpp-decline',
    label: 'No thanks',
    checked: !isHdppSelected
  }];

  LIFE_CYCLE_EVENT_BUS.on('dual-path.change', ({ output } = {}) => {
    setIsDualPathSelected(output?.isOptedIn);
  });

  const handleChange = (evt) => {
    const userSelectedHdpp = evt.target.value === 'hdpp-accept';
    let checked = userSelectedHdpp && !isHdppSelected;
    onChange({
      itemId,
      checked,
      addOnType: HDPP
    });
    LIFE_CYCLE_EVENT_BUS.trigger('hdpp.change', { isOptedIn: checked, itemId });
    setIsHdppSelected(checked);

  };

  useEffect(() => {
    const tippy = tooltipButtonRef?.current?._tippy;
    if (tippy) tippy.hide();
  }, [isHdppSelected]);

  const tooltip = (displayNewHdppProvider
    ? (
      <ProtectionPlanTooltip
        channel={channel}
        ref={tooltipButtonRef}
      />
    )
    : (
      <CurrentProtectionPlanTooltip
        channel={channel}
        url={url}
        ref={tooltipButtonRef}
      />
    )

  );

  if (isDualPathSelected) {
    return null;
  }

  return (
    <ProductAddOnsPod
      heading="Protect This Item"
      subHeading={subHeading}
      tooltip={tooltip}
      userOptions={userOptions}
      icon={iconUrl}
      iconHeight={47}
      onChange={handleChange}
      channel={channel}
      radio
    />
  );
};

HomeDepotProtectionPlan.displayName = 'productAddOns-homeDepotProtectionPlan';

HomeDepotProtectionPlan.propTypes = {
  /** Device Type: Mobile or Desktop */
  channel: oneOf(['desktop', 'mobile']),
  /** protection plan price */
  price: string.isRequired,
  /** protection plan term length */
  term: string,
  /** handler for select or unselect hdpp */
  onChange: func.isRequired,
  /** protection plan item id */
  itemId: string.isRequired,
  /** url for plan details */
  url: string,
  /** FS for new hdpp provider */
};

HomeDepotProtectionPlan.defaultProps = {
  channel: 'desktop',
  term: '',
  url: '',
};